import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, IconButton, TableCell, Typography } from '@mui/material';
import { type MouseEvent, type ReactNode, memo } from 'react';

import {
    formatReportCurrency,
    formatReportPercent,
} from 'shared/lib/reportFormatters';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import { lockedColumnWidth } from './columnWidth';
import { getDefaultAlign } from './getDefaultAlign';

import type {
    ReportWidgetColumnConfig,
    ReportWidgetItem,
} from '../../model/types';

interface ReportWidgetTableCellProps {
    item: ReportWidgetItem;
    column: ReportWidgetColumnConfig;
    showColorSwatchInLabelColumn?: ReportWidgetColumnConfig['valueKey'];
    swatchColor?: string;
    onLabelClick?: () => void;
    isPrinting?: boolean;
}

function getCellValue(
    item: ReportWidgetItem,
    col: ReportWidgetColumnConfig,
): number | string {
    if (col.valueKey === 'primaryValue') return item.primaryValue;
    if (col.valueKey === 'label') return item.label;
    return item.secondaryValues?.[col.valueKey] ?? '';
}

function formatCell(
    value: number | string,
    format?: ReportWidgetColumnConfig['format'],
): string {
    if (value === '' || value == null) return '-';
    switch (format) {
        case 'currency': {
            const num =
                typeof value === 'number'
                    ? value
                    : Number.parseFloat(String(value));
            if (Number.isNaN(num)) return String(value);
            return formatReportCurrency(num);
        }

        case 'number':
            return new Intl.NumberFormat().format(
                typeof value === 'number'
                    ? value
                    : Number.parseFloat(String(value)) || 0,
            );

        case 'percent': {
            const num =
                typeof value === 'number'
                    ? value
                    : Number.parseFloat(String(value));
            if (Number.isNaN(num)) return String(value);
            return formatReportPercent(num);
        }

        default:
            return String(value);
    }
}

const stopRowClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
};

const CellTooltip = ({
    content,
    tooltip,
    showTooltipIcon,
    label,
}: {
    content: JSX.Element;
    tooltip: ReactNode;
    showTooltipIcon?: boolean;
    label: string;
}) => {
    const body = showTooltipIcon ? (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>{content}</Box>
            <Box component="span" onClick={stopRowClick} sx={{ flexShrink: 0 }}>
                <IconButton
                    aria-label={label}
                    size="small"
                    disableRipple
                    disableFocusRipple
                    sx={{
                        padding: 0,
                        minWidth: '1.6rem',
                        width: '1.6rem',
                        height: '1.6rem',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        '&.Mui-focusVisible': {
                            backgroundColor: 'transparent',
                        },
                    }}
                >
                    <InfoOutlinedIcon
                        sx={{ fontSize: '1.6rem', color: '#B7B7B7' }}
                    />
                </IconButton>
            </Box>
        </Box>
    ) : (
        content
    );

    return <DynamicTooltip content={tooltip}>{body}</DynamicTooltip>;
};

function renderClickableContent(params: {
    onLabelClick?: () => void;
    baseContent: JSX.Element;
    column: ReportWidgetColumnConfig;
}): JSX.Element {
    const { onLabelClick, baseContent, column } = params;

    if (!onLabelClick) {
        return baseContent;
    }

    return (
        <Box
            onClick={(e) => {
                e.stopPropagation();
                onLabelClick();
            }}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
                cursor: 'pointer',
                '& .label-text': {
                    transition: 'color 0.15s ease, text-decoration 0.15s ease',
                },
                '& .details-icon': {
                    opacity: 0,
                    transition: 'opacity 0.15s ease',
                },
                '&:hover .label-text': {
                    textDecoration: 'underline',
                    color: 'text.primary',
                },
                '&:hover .details-icon': {
                    opacity: 1,
                },
            }}
        >
            <Box className="label-text" sx={{ minWidth: 0 }}>
                {baseContent}
            </Box>
            <LaunchIcon
                className="details-icon"
                aria-label={column.label}
                sx={{
                    fontSize: '1.4rem',
                    verticalAlign: 'middle',
                }}
            />
        </Box>
    );
}

export const ReportWidgetTableCell = memo(
    ({
        item,
        column,
        showColorSwatchInLabelColumn,
        swatchColor,
        onLabelClick,
        isPrinting = false,
    }: ReportWidgetTableCellProps) => {
        const value = getCellValue(item, column);
        const formatted = formatCell(value, column.format);
        const cellTooltip = column.getCellTooltip?.(item) ?? null;
        const customContent = column.renderCell?.(item, { isPrinting });

        const showSwatch =
            showColorSwatchInLabelColumn === column.valueKey && swatchColor;

        const makeBold = Boolean(column.bold || column.highlightColor);
        const shouldWrap = column.allowWrap === true;
        const hasLineClamp =
            shouldWrap && column.wrapLines != null && column.wrapLines > 0;
        const lineClamp = hasLineClamp ? column.wrapLines : null;
        const align = getDefaultAlign(column);
        const lockedWidth = isPrinting ? undefined : lockedColumnWidth(column);

        if (customContent !== undefined) {
            return (
                <TableCell
                    align={align}
                    onClick={stopRowClick}
                    sx={{
                        width:
                            lockedWidth == null
                                ? undefined
                                : `${lockedWidth}px`,
                        minWidth:
                            isPrinting || column.minWidth == null
                                ? undefined
                                : `${column.minWidth}px`,
                        maxWidth:
                            isPrinting || column.maxWidth == null
                                ? undefined
                                : `${column.maxWidth}px`,
                        verticalAlign: column.verticalAlign ?? 'top',
                    }}
                >
                    {customContent}
                </TableCell>
            );
        }

        const textStyles = {
            fontWeight: makeBold ? 500 : undefined,
            minWidth: 0,
            whiteSpace: shouldWrap ? 'normal' : 'nowrap',
            overflowWrap: shouldWrap && !isPrinting ? 'anywhere' : undefined,
            wordBreak: shouldWrap && !isPrinting ? 'break-word' : undefined,
            ...(lockedWidth != null && !shouldWrap
                ? {
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                  }
                : {}),
            ...(lineClamp == null
                ? {}
                : {
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitLineClamp: lineClamp,
                      WebkitBoxOrient: 'vertical',
                  }),
        };

        const baseContent = showSwatch ? (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: swatchColor ?? 'action.hover',
                        flexShrink: 0,
                        marginTop: '0.35em',
                    }}
                />
                <Typography variant="body2" component="span" sx={textStyles}>
                    {formatted}
                </Typography>
            </Box>
        ) : (
            <Typography variant="body2" component="span" sx={textStyles}>
                {formatted}
            </Typography>
        );

        const clickableContent = renderClickableContent({
            onLabelClick,
            baseContent,
            column,
        });

        const cellContent = cellTooltip ? (
            <CellTooltip
                content={clickableContent}
                tooltip={cellTooltip}
                showTooltipIcon={column.showTooltipIcon}
                label={column.label}
            />
        ) : (
            clickableContent
        );

        return (
            <TableCell
                align={align}
                sx={{
                    fontWeight:
                        column.bold || column.highlightColor ? 500 : undefined,
                    color: column.highlightColor ?? undefined,
                    width: lockedWidth == null ? undefined : `${lockedWidth}px`,
                    minWidth:
                        isPrinting || column.minWidth == null
                            ? undefined
                            : `${column.minWidth}px`,
                    maxWidth:
                        isPrinting || column.maxWidth == null
                            ? undefined
                            : `${column.maxWidth}px`,
                    verticalAlign: 'top',
                }}
            >
                <Box
                    sx={{
                        ...(lockedWidth == null
                            ? {
                                  display: 'inline-block',
                                  maxWidth: '100%',
                              }
                            : {
                                  width: 0,
                                  minWidth: '100%',
                                  overflow: 'hidden',
                              }),
                        textAlign: align === 'right' ? 'right' : 'left',
                    }}
                >
                    {cellContent}
                </Box>
            </TableCell>
        );
    },
);
